/**
 * useDropsFiltering
 * For Media Devoted | Created By Ciro Autiero
 *
 * Client-side filtering + pagination over a fetched batch.
 */

import { useEffect, useMemo, useRef } from 'react';

import { dropInDateRange } from '@trafficpulse/shared';

import type { DropsSortState } from '@/features/drops/utils/sort';

import type { DropStatusLabel, SmswiserDrop } from '@/types/smswiser-drops';
import { deriveDropStatusLabel } from '@/features/drops/utils/dropStatus';
import { isDefaultDropsSort, sortDrops } from '@/features/drops/utils/sort';

export interface DropsFilterState {
  search: string;
  status: 'all' | DropStatusLabel;
  campaign: string;
  leadsList: string;
  sender: string;
  country: string;
  /** YYYY-MM-DD, empty when no date filter. Both must be set for filtering. */
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_DROPS_FILTERS: DropsFilterState = {
  search: '',
  status: 'all',
  campaign: '',
  leadsList: '',
  sender: '',
  country: '',
  dateFrom: '',
  dateTo: '',
};

function ymdToRangeIso(dateFrom: string, dateTo: string): { from: string; to: string } | null {
  if (!dateFrom || !dateTo) return null;
  return {
    from: `${dateFrom}T00:00:00.000Z`,
    to: `${dateTo}T23:59:59.999Z`,
  };
}

export function useDropsFiltering(params: {
  drops: SmswiserDrop[];
  filters: DropsFilterState;
  pageSize: number;
  sort: DropsSortState;
  page: number;
  setPage: (value: number | ((prev: number) => number)) => void;
}) {
  const { drops, filters, pageSize, sort, page, setPage } = params;

  const filterKey = JSON.stringify({ filters, sort });
  const prevFilterKeyRef = useRef(filterKey);

  // Reset to page 1 when filters/sort change (not on initial mount / URL restore).
  useEffect(() => {
    if (prevFilterKeyRef.current === filterKey) return;
    prevFilterKeyRef.current = filterKey;
    setPage(1);
  }, [filterKey, setPage]);

  const normalized = useMemo(() => {
    return {
      search: filters.search.trim().toLowerCase(),
      campaign: filters.campaign.trim().toLowerCase(),
      leadsList: filters.leadsList.trim().toLowerCase(),
      sender: filters.sender.trim().toLowerCase(),
      country: filters.country.trim().toLowerCase(),
    };
  }, [filters]);

  const dateRange = useMemo(
    () => ymdToRangeIso(filters.dateFrom, filters.dateTo),
    [filters.dateFrom, filters.dateTo],
  );

  const filtered = useMemo(() => {
    return drops.filter((d) => {
      const status = deriveDropStatusLabel(d);
      if (filters.status !== 'all' && status !== filters.status) return false;

      const senders = (d.senderName || []).join(' ').toLowerCase();
      const matchesCampaign =
        !normalized.campaign || (d.campaign || '').toLowerCase().includes(normalized.campaign);
      const matchesLeads =
        !normalized.leadsList || (d.leadsList || '').toLowerCase().includes(normalized.leadsList);
      const matchesSender = !normalized.sender || senders.includes(normalized.sender);
      const matchesCountry =
        !normalized.country || (d.country || '').toLowerCase().includes(normalized.country);

      if (!matchesCampaign || !matchesLeads || !matchesSender || !matchesCountry) return false;

      if (dateRange) {
        const dropForRange = {
          finishedAt: d.finishedAt,
          executeAfter: d.executeAfter,
          status: d.status ? { startedAt: d.status.startedAt ?? null } : null,
        };
        if (!dropInDateRange(dropForRange, dateRange.from, dateRange.to)) return false;
      }

      if (!normalized.search) return true;

      const haystack = [
        d.title,
        d.uniqueId,
        d.campaign,
        d.leadsList,
        d.country,
        senders,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalized.search);
    });
  }, [drops, filters.status, normalized, dateRange]);

  const sorted = useMemo(
    () =>
      sortDrops(filtered, sort, {
        preserveSmswiserOrder: isDefaultDropsSort(sort),
      }),
    [filtered, sort],
  );

  const totalPages = useMemo(() => Math.max(1, Math.ceil(sorted.length / pageSize)), [
    sorted.length,
    pageSize,
  ]);

  const clampedPage = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const startIdx = (clampedPage - 1) * pageSize;
    return sorted.slice(startIdx, startIdx + pageSize);
  }, [sorted, clampedPage, pageSize]);

  const canPrev = clampedPage > 1;
  const canNext = clampedPage < totalPages;

  return {
    filteredDrops: sorted,
    pagedDrops: paged,
    page: clampedPage,
    setPage,
    totalPages,
    canPrev,
    canNext,
  };
}
